import { DocumentNode } from "@apollo/client"
import {
  BlockTypeFieldType,
  ServerBlockTypeType
} from "@pathwright/blocks-core"
import gql from "graphql-tag"
import upperFirst from "lodash/upperFirst"
import { Scope } from "./scopes"

// hack to fix an import error
export { parse } from "graphql/language"

// ** Blocks Content ** //

export const CONTENT_BLOCK_FIELDS_FRAGMENT = gql`
  fragment contentBlockFields on ContentBlock {
    id
    contentID
    createdBy
    createdDateTime
    lastModifiedDateTime
    lastModifiedBy
    order
    position
    type
    data
    layout
    style {
      selectedSettings
      styleAttributes
    }
    completion {
      progress
      score
    }
    migratedFromFirebaseId
  }
`

export const CONTENT_FIELDS_FRAGMENT = gql`
  fragment contentFields on Content {
    id
    createdBy
    accountID
    copiedFromAccountID
    createdDateTime
    lastPublishedDateTime
    lastModifiedDateTime
    accessToken
    completion {
      completionToken
      progress
      score
    }
    blocks {
      ...contentBlockFields
    }
    changes
    contextKey
    contentNotice
  }
  ${CONTENT_BLOCK_FIELDS_FRAGMENT}
`

export const BLOCKS_CONTEXT_FRAGMENT = gql`
  fragment contextFields on BlocksContext {
    accountID
    userID
    accessToken
    mediaStoragePath
    contentStatus
    contentTarget
    contentTargetID
    contentRouteToken
    blockTypes {
      type
      layouts
      menu
      helpLink
      fields
      defaults
    }
  }
`

export const blocksContentQuery = gql`
  query BlocksContent(
    $id: ID
    $mode: MODE!
    $contextKey: String!
    $routeToken: String
    $template: String
    $draft: Boolean!
    $upsert: Boolean
    $userID: ID
  ) {
    blocksContext: BlocksContext(contextKey: $contextKey) {
      ...contextFields
    }
    content: Content(
      id: $id
      mode: $mode
      contextKey: $contextKey
      routeToken: $routeToken
      template: $template
      draft: $draft
      upsert: $upsert
      userID: $userID
    ) {
      ...contentFields
    }
  }
  ${BLOCKS_CONTEXT_FRAGMENT}
  ${CONTENT_FIELDS_FRAGMENT}
`

export const blocksContextQuery = gql`
  query BlocksContext($contextKey: String) {
    blocksContext: BlocksContext(contextKey: $contextKey) {
      ...contextFields
    }
  }
  ${BLOCKS_CONTEXT_FRAGMENT}
`

export const blocksContentOnlyQuery = gql`
  query BlocksContentOnly(
    $id: ID
    $mode: MODE!
    $contextKey: String!
    $routeToken: String
    $template: String
    $draft: Boolean!
    $upsert: Boolean
    $userID: ID
  ) {
    content: Content(
      id: $id
      mode: $mode
      contextKey: $contextKey
      routeToken: $routeToken
      template: $template
      draft: $draft
      upsert: $upsert
      userID: $userID
    ) {
      ...contentFields
    }
  }
  ${CONTENT_FIELDS_FRAGMENT}
`

export const blocksContentListQuery = gql`
  query BlocksContentList(
    $contextKeys: [String!]
    $mode: MODE!
    $draft: Boolean!
    $userID: ID
    $limit: Int
    $offset: Int
    $postgresOnly: Boolean
  ) {
    contentList: ContentList(
      contextKeys: $contextKeys
      mode: $mode
      draft: $draft
      userID: $userID
      limit: $limit
      offset: $offset
      postgresOnly: $postgresOnly
    ) {
      ...contentFields
    }
  }
  ${CONTENT_FIELDS_FRAGMENT}
`

export const blocksResponseExportQuery = gql`
  query BlocksResponseExport(
    $contextKey: String!
    $input: BlocksResponseExportInput!
  ) {
    blocksResponseExport(contextKey: $contextKey, input: $input) {
      url
    }
  }
`

export const blocksResponseIndexQuery = gql`
  query BlocksResponseIndex(
    $contextKey: String!
    $input: BlocksResponseIndexInput!
  ) {
    blocksResponseIndex(contextKey: $contextKey, input: $input) {
      resourceId
      resourceName
      items {
        lessonId
        lessonName
        lessonOrder
        stepId
        stepName
        stepOrder
        blockId
        blockType
        blockLayout
        blockOrder
        responseCount
      }
    }
  }
`

export const blockTypeMetadataQuery = gql`
  query BlockTypeMetadata($userBlockTypesOnly: Boolean) {
    blockTypeMetadata(userBlockTypesOnly: $userBlockTypesOnly) {
      type
      hasUserData
    }
  }
`

export const blocksPathMigrationMutation = gql`
  mutation BlocksPathMigration(
    $contextKey: String!
    $input: BlocksPathMigrationInput!
  ) {
    blocksPathMigration(contextKey: $contextKey, input: $input) {
      taskIds
    }
  }
`

export const blocksResourceMigrationMutation = gql`
  mutation BlocksResourceMigration(
    $contextKey: String!
    $input: BlocksResourceMigrationInput!
  ) {
    blocksResourceMigration(contextKey: $contextKey, input: $input) {
      taskIds
    }
  }
`

export const contentPublishMutation = gql`
  mutation PublishContent($id: ID!, $contextKey: String!, $routeToken: String) {
    content: publishContent(
      id: $id
      contextKey: $contextKey
      routeToken: $routeToken
    ) {
      ...contentFields
    }
  }
  ${CONTENT_FIELDS_FRAGMENT}
`

export const discardDraftContentMutation = gql`
  mutation DiscardDraftContent(
    $contentID: ID!
    $contextKey: String!
    $routeToken: String
  ) {
    content: discardDraftContent(
      contentID: $contentID
      contextKey: $contextKey
      routeToken: $routeToken
    ) {
      ...contentFields
    }
  }
  ${CONTENT_FIELDS_FRAGMENT}
`

// Move, Delete, Paste Block Mutations
export const getRootBlockMutations = (): {
  moveBlock: DocumentNode
  deleteBlock: DocumentNode
  pasteBlock: DocumentNode
  pasteBlocks: DocumentNode
} => {
  // Move Block
  const moveBlockGQL: DocumentNode = gql`
    mutation MoveBlock(
      $contentID: ID!
      $id: ID!
      $contextKey: String!
      $routeToken: String
      $order: Int!
    ) {
      content: moveBlock(
        contentID: $contentID
        id: $id
        contextKey: $contextKey
        routeToken: $routeToken
        order: $order
      ) {
        ...contentFields
      }
    }
    ${CONTENT_FIELDS_FRAGMENT}
  `

  // Delete Block Mutation
  const deleteBlockGQL: DocumentNode = gql`
    mutation DeleteBlock(
      $contentID: ID!
      $contextKey: String!
      $routeToken: String
      $id: ID!
    ) {
      content: deleteBlock(
        contentID: $contentID
        contextKey: $contextKey
        routeToken: $routeToken
        id: $id
      ) {
        ...contentFields
      }
    }
    ${CONTENT_FIELDS_FRAGMENT}
  `

  const pasteBlockGQL: DocumentNode = gql`
    mutation PasteBlock(
      $sourceContentID: ID!
      $sourceBlockID: ID!
      $destinationContentID: ID!
      $order: Int!
      $contextKey: String!
      $routeToken: String
    ) {
      pasteBlock(
        sourceContentID: $sourceContentID
        sourceBlockID: $sourceBlockID
        destinationContentID: $destinationContentID
        order: $order
        contextKey: $contextKey
        routeToken: $routeToken
      ) {
        ...contentBlockFields
      }
    }
    ${CONTENT_BLOCK_FIELDS_FRAGMENT}
  `

  const pasteBlocksGQL: DocumentNode = gql`
    mutation PasteBlocks(
      $sourceContentID: ID!
      $sourceBlockID: ID!
      $destinationContentID: ID!
      $startingOrder: Int!
      $copies: Int!
      $contextKey: String!
      $routeToken: String
    ) {
      pasteBlocks(
        sourceContentID: $sourceContentID
        sourceBlockID: $sourceBlockID
        destinationContentID: $destinationContentID
        startingOrder: $startingOrder
        copies: $copies
        contextKey: $contextKey
        routeToken: $routeToken
      ) {
        ...contentBlockFields
      }
    }
    ${CONTENT_BLOCK_FIELDS_FRAGMENT}
  `

  return {
    moveBlock: moveBlockGQL,
    deleteBlock: deleteBlockGQL,
    pasteBlock: pasteBlockGQL,
    pasteBlocks: pasteBlocksGQL
  }
}

export type BlockTypeMutationWithNameType = {
  name: string
  gql: DocumentNode
}

// Auto-generated saveBlock mutations for each block type
export const getBlockMutationsForType = (
  blockType: ServerBlockTypeType
): BlockTypeMutationWithNameType[] => {
  const mutations: BlockTypeMutationWithNameType[] = []

  const typeName: string = upperFirst(blockType.type)

  // Content scoped block mutations
  const saveBlockMutationName = `save${typeName}Block`
  const saveBlockGQL: DocumentNode = gql`
    mutation ${saveBlockMutationName}($contentID: ID!, $id: ID!, $contextKey: String!, $routeToken: String, $order: Int, $layout: String!, $data: ${typeName}Input, $style: BlockStyleInput) {
      block: ${saveBlockMutationName}(contentID: $contentID, id: $id, contextKey: $contextKey, routeToken: $routeToken, order: $order, data: $data, layout: $layout, style: $style) {
        ...contentBlockFields

        # return latest list of changes for content triggered by this mutation
        contentChanges
      }
    }
    ${CONTENT_BLOCK_FIELDS_FRAGMENT}
  `

  mutations.push({
    name: saveBlockMutationName,
    gql: saveBlockGQL
  })

  // User scoped block mutations
  const userScopedFields: BlockTypeFieldType[] = Object.values(
    blockType.fields
  ).filter((field: BlockTypeFieldType) => field.scope === Scope.USER)
  if (userScopedFields.length > 0) {
    const saveUserDataMutationName = `save${typeName}BlockUserData`
    const saveUserDataGQL: DocumentNode = gql`
      mutation ${saveUserDataMutationName}($contentID: ID!, $contextKey: String!, $routeToken: String, $id: ID!, $userID: ID!, $noProgress: Boolean!, $order: Int, $data: ${typeName}UserDataInput, $draft: Boolean) {
        block: ${saveUserDataMutationName}(contentID: $contentID, id: $id, contextKey: $contextKey, routeToken: $routeToken, noProgress: $noProgress, order: $order, data: $data, userID: $userID, draft: $draft) {
          ...contentBlockFields

          contentCompletion @skip(if: $noProgress) {
            completionToken
            progress
            score
          }
        }
      }
      ${CONTENT_BLOCK_FIELDS_FRAGMENT}
    `

    mutations.push({
      name: saveUserDataMutationName,
      gql: saveUserDataGQL
    })
  }

  return mutations
}

// ** MUX Video ** //
export const MUX_VIDEOS_QUERY = gql`
  query Videos {
    muxVideos {
      id
      name
      asset {
        id
        status
      }
      upload {
        id
        status
        url
      }
    }
  }
`

export const MUX_VIDEO_EMBED_QUERY = gql`
  query Embed($id: ID!) {
    muxVideoEmbed(id: $id) {
      id
      status
      duration
      download {
        status
        url
      }
      thumb {
        src
        gifSrc
      }
      stream {
        url
        status
        size {
          maxWidth
          maxHeight
          aspectRatio
        }
      }
      audio {
        duration
        status
        maxChannelLayout
        maxChannels
      }
      subtitles
    }
  }
`

export const CREATE_VIDEO_MUTATION = gql`
  mutation CreateVideo($name: String, $contextKey: String!) {
    createMuxVideo(name: $name, contextKey: $contextKey) {
      id
      name
      upload {
        url
        id
        status
      }
    }
  }
`
export const DELETE_VIDEO_MUTATION = gql`
  mutation DeleteVideo($id: ID!) {
    deleteMuxVideo(id: $id)
  }
`

export const CHECK_AND_UPDATE_VIDEO_STATUS_MUTATION = gql`
  mutation VideoStatus($assetID: ID!) {
    checkAndUpdateVideoStatus(assetID: $assetID) {
      encoding {
        status
      }
    }
  }
`
